/**
 * Adds a lane to an auction in local storage.
 *
 * @param {string} auctionID - The ID of the auction.
 * @param {string} laneID - The ID of the lane to add to the auction.
 */
export const addLaneToAuctionInLocalStorage = ({key, laneID}) => {
    try {
        const activeAuctions = JSON.parse(window.localStorage.getItem('activeAuctions') || '[]');

        // Find the auction in local storage, if it exists
        const auctionEntry = activeAuctions.find((auction) => auction.key === key);

        if (auctionEntry) {
            // If auction already has lanes, add laneID if it doesn't exist
            if (!auctionEntry?.lanes?.includes(laneID)) {
                auctionEntry.lanes.push(laneID);
            }
        } else {
            // If auction doesn't exist in local storage, add it with the new lane
            activeAuctions.push({ key, lanes: [laneID] });
        }

        // Update local storage with the modified activeAuctions array
        window.localStorage.setItem('activeAuctions', JSON.stringify(activeAuctions));
    } catch (error) {
        console.error('Error adding lane to auction in local storage:', error);
    }
};

function updateKeyInLocalStorage(storageKey, currentKey, newKey) {
    // Retrieve the object from localStorage
    const storedObject = JSON.parse(localStorage.getItem(storageKey)) || {};

    // Check if the current key exists
    if (storedObject.hasOwnProperty(currentKey)) {
        // Get the value associated with the current key
        const value = storedObject[currentKey];

        // Delete the old key
        delete storedObject[currentKey];

        // Add the new key with the old value
        storedObject[newKey] = value;

        // Save the updated object back to localStorage
        localStorage.setItem(storageKey, JSON.stringify(storedObject));
        console.log(`Key "${currentKey}" updated to "${newKey}".`);
    } else {
        console.log(`Key "${currentKey}" not found.`);
    }
}

/**
 * Adds or updates an auction in localStorage under the 'activeAuctions' key.
 * Handles updating the key if an auction with a mismatching key already exists,
 * and migrates lanes if needed. Adds an expiry timestamp 8 hours from now.
 *
 * @param {Object} params - Parameters for the function.
 * @param {string} params.key - Unique key for the auction.
 * @param {string} params.auctionID - Auction ID to be added/updated.
 * @param {string|null} [params.laneID] - Optional lane ID to associate with the auction.
 */
export const addAuctionToLocalStorage = ({ key, auctionID, laneID = null }) => {
    try {
        // Retrieve the current saved auctions from localStorage, defaulting to an empty array
        const savedAuctions = JSON.parse(window.localStorage.getItem('activeAuctions') || '[]');

        /** STEP 1: Add a timestamp, so we can expire it later */
        // const timestamp = Date.now() + 8 * 60 * 60 * 1000; // 8 hours in milliseconds
        const timestamp = Date.now();

        /** STEP 2: Handle mismatching keys for the same auctionID */
        const existsIndex = savedAuctions.findIndex(
            (auction) => auction.auctionID === auctionID && auction.key !== auctionID
        );

        let migrateLanes = []; // Array to hold lanes for migration, if needed

        // if (existsIndex !== -1) {
        //     /** Update the key in 'auctionTokens' if a mismatch is found */
        //     updateKeyInLocalStorage('auctionTokens', savedAuctions[existsIndex].key, auctionID);
        //
        //     migrateLanes = savedAuctions[existsIndex]?.lanes;
        //     savedAuctions.splice(existsIndex, 1); // Remove old auction with mismatching key
        // }

        /** STEP 3: Check if the auction already exists */
        const auctionIndex = savedAuctions.findIndex((auction) => auction.key === key);

        if (auctionIndex === -1) {
            /** Add a new auction if it doesn't exist */
            const newAuction = {
                key,
                auctionID,
                lanes: laneID ? [laneID] : [...migrateLanes],
                timestamp: timestamp, // Add expiry timestamp
            };
            savedAuctions.push(newAuction);
        } else {
            /** Update an existing auction */
            const auction = savedAuctions[auctionIndex];

            // Update auctionID if it's missing
            if (auctionID && !auction.auctionID) {
                auction.auctionID = auctionID;
            }

            // Merge lanes from the migrated auction
            if (migrateLanes.length > 0) {
                auction.lanes = [...auction.lanes, ...migrateLanes];
            }

            // Add the laneID if it's not already in the lanes array
            if (laneID && !auction.lanes.includes(laneID)) {
                auction.lanes.push(laneID);
            }

            // Update the expiry timestamp
            auction.timestamp = timestamp;
        }

        /** Save the updated list back to localStorage */
        window.localStorage.setItem('activeAuctions', JSON.stringify(savedAuctions));
    } catch (error) {
        /** Handle errors */
        console.log('local storage error>>', error.message);
    }
};


/**
 * Removes a specific lane from an auction in local storage.
 *
 * @param {string} auctionID - The ID of the auction.
 * @param {string} laneID - The ID of the lane to remove from the auction.
 */
export const removeLaneFromAuctionInLocalStorage = (auctionID, laneID) => {
    try {
        const activeAuctions = JSON.parse(window.localStorage.getItem('activeAuctions') || '[]');

        // Find the auction in local storage, if it exists
        const auctionEntry = activeAuctions.find((auction) => auction.key === auctionID);

        if (auctionEntry && auctionEntry.lanes.includes(laneID)) {
            // Remove the laneID from the lanes array
            auctionEntry.lanes = auctionEntry.lanes.filter((lane) => lane !== laneID);

            // Update local storage regardless of whether there are lanes left
            window.localStorage.setItem('activeAuctions', JSON.stringify(activeAuctions));
        }
    } catch (error) {
        console.error('Error removing lane from auction in local storage:', error);
    }
};

/**
 * Stores a token associated with a specific auctionID in local storage.
 * @param {string} auctionID - The unique ID of the auction.
 * @param {string} token - The token to be stored for the auction.
 */
export const setTokenByAuctionID = (auctionID, token) => {
    if (!auctionID || !token) {
        console.error('AuctionID and token are required to set a token.');
        return;
    }

    const storedTokens = JSON.parse(window.localStorage.getItem('auctionTokens') || '{}');
    storedTokens[auctionID] = token;
    window.localStorage.setItem('auctionTokens', JSON.stringify(storedTokens));
};

/**
 * Retrieves a token associated with a specific auctionID from local storage.
 * @param {string} auctionID - The unique ID of the auction.
 * @returns {string|null} The token associated with the auctionID, or null if not found.
 */
export const getTokenByAuctionID = (auctionID) => {
    if (!auctionID) {
        console.error('AuctionID is required to get a token.');
        return null;
    }

    const storedTokens = JSON.parse(window.localStorage.getItem('auctionTokens') || '{}');
    return storedTokens[auctionID] || null;
};

/**
 * Retrieves the first token from the stored tokens in local storage.
 * @returns {string|null} The first token found, or null if no tokens are stored.
 */
export const getSingleToken = () => {
    const storedTokens = JSON.parse(window.localStorage.getItem('auctionTokens') || '{}');
    const tokensArray = Object.values(storedTokens); // Extract the values (tokens) as an array
    return tokensArray.length > 0 ? tokensArray[0] : null; // Return the first token if it exists
};

/**
 * Retrieves active auctions from local storage that are within the specified time window.
 * Auctions older than the provided number of hours will be removed from storage.
 *
 * @param {number} hours - The number of hours to use as the expiry window (e.g., 8 for 8 hours).
 * @returns {Array<Object>} - An array of active auctions still within the valid time window.
 *
 * @example
 * const activeAuctions = getActiveAuctions(8);
 * console.log(activeAuctions); // Logs auctions active within the last 8 hours.
 */
export const getActiveAuctions = (hours) => {
    // Retrieve saved auctions or default to an empty array
    let savedAuctions = JSON.parse(localStorage.getItem('activeAuctions') || '[]');

    // Convert the expiry window to milliseconds
    const expiryWindowInMs = hours * 60 * 60 * 1000;

    // Get the current timestamp
    const now = Date.now();

    // Filter auctions to keep only the ones within the valid time window
    savedAuctions = savedAuctions.filter((auction) => {
        const isWithinExpiry = now - auction.timestamp <= expiryWindowInMs;

        if (!isWithinExpiry) {
            console.log(`Auction expired and removed: ${auction.key}`);
        }

        return isWithinExpiry;
    });

    // Save the updated list of active auctions back to local storage
    localStorage.setItem('activeAuctions', JSON.stringify(savedAuctions));

    // Return the list of valid active auctions
    return savedAuctions;
};

